import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertCircle, ChevronDown, ChevronUp, Gift, Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import { cn } from '@/lib/utils';
import { formatDate } from '~/lib/format-date';
import { useLoyaltyProductsList } from '~/hooks/use-loyalty-products';
import type { SortDir } from '~/lib/grid/sort';

const PAGE_SIZES = [25, 50, 100];

/** Grid fields the controller maps server-side (pname → ProductName, …). */
type SortKey = 'pname' | 'pcode' | 'bcd' | 'pnt' | 'afrom' | 'ato';

/** Loyalty product points (Πόντοι προϊόντων) — read-only, server-paged grid of current points, or every row. */
export function LoyaltyProductsGridView() {
	const { t } = useTranslation();

	const [searchInput, setSearchInput] = useState('');
	const [search, setSearch] = useState('');
	const [page, setPage] = useState(0);
	const [pageSize, setPageSize] = useState(25);
	const [sortKey, setSortKey] = useState<SortKey | null>('pname');
	const [sortDir, setSortDir] = useState<SortDir>('asc');
	const [history, setHistory] = useState(false);

	useEffect(() => {
		const handle = setTimeout(() => {
			setSearch(searchInput.trim());
			setPage(0);
		}, 350);
		return () => clearTimeout(handle);
	}, [searchInput]);

	const { data, isLoading, isError, refetch, isFetching } = useLoyaltyProductsList({
		skip: page * pageSize,
		take: pageSize,
		search,
		history,
		sortSelector: sortKey,
		sortDir
	});

	const rows = data?.items ?? [];
	const total = data?.totalCount ?? 0;
	const pageCount = Math.max(1, Math.ceil(total / pageSize));

	const toggleSort = (key: SortKey) => {
		if (sortKey === key) setSortDir(d => (d === 'asc' ? 'desc' : 'asc'));
		else {
			setSortKey(key);
			setSortDir('asc');
		}
		setPage(0);
	};

	const SortHeader = ({ col, label, className }: { col: SortKey; label: string; className?: string }) => (
		<button type="button" onClick={() => toggleSort(col)} className={cn('inline-flex items-center gap-1 font-medium', className)}>
			{label}
			{sortKey === col && (sortDir === 'asc' ? <ChevronUp size={13} /> : <ChevronDown size={13} />)}
		</button>
	);

	return (
		<div className="flex flex-col gap-3">
			<div className="flex items-start justify-between gap-4 border-b border-border pb-4">
				<div>
					<h1 className="text-2xl font-semibold tracking-[-0.015em] text-foreground">{t('loyalty.products.title')}</h1>
					<p className="mt-1 text-sm text-foreground-muted">{t('loyalty.products.subtitle', { count: total })}</p>
				</div>
			</div>

			<div className="flex flex-wrap items-center gap-2 rounded-md border border-border bg-surface p-3">
				<div className="relative min-w-60 flex-1 md:max-w-md">
					<Search size={16} className="pointer-events-none absolute top-1/2 left-2.5 -translate-y-1/2 text-foreground-subtle" />
					<Input
						value={searchInput}
						onChange={e => setSearchInput(e.target.value)}
						placeholder={t('loyalty.products.searchPlaceholder')}
						className="pl-8"
					/>
				</div>
				<label className="flex items-center gap-2 text-sm text-foreground-muted">
					<Switch
						checked={history}
						onCheckedChange={checked => {
							setHistory(checked);
							setPage(0);
						}}
					/>
					{t('loyalty.products.showHistory')}
				</label>
			</div>

			<div className="overflow-x-auto rounded-md border border-border bg-surface">
				{isError ? (
					<div className="flex flex-col items-center gap-3 py-16 text-center">
						<AlertCircle size={40} className="text-danger-fg" strokeWidth={1.25} />
						<p className="text-sm text-foreground-muted">{t('loyalty.loadError')}</p>
						<Button variant="outline" onClick={() => void refetch()}>
							{t('common.retry')}
						</Button>
					</div>
				) : isLoading ? (
					<div className="flex flex-col gap-2 p-3">
						{Array.from({ length: 10 }).map((_, i) => (
							<Skeleton key={i} className="h-10 w-full rounded-md" />
						))}
					</div>
				) : rows.length === 0 ? (
					<div className="flex flex-col items-center gap-3 py-16 text-center">
						<Gift size={36} className="text-foreground-disabled" strokeWidth={1.25} />
						<p className="text-sm text-foreground-muted">{t('common.noResults')}</p>
					</div>
				) : (
					<table className={cn('w-full border-collapse text-sm', isFetching && 'opacity-60')}>
						<thead className="bg-elevated text-xs text-foreground-muted">
							<tr className="border-b border-border">
								<th className="px-3 py-2 text-left">
									<SortHeader col="pname" label={t('loyalty.products.columns.name')} />
								</th>
								<th className="px-3 py-2 text-left">
									<SortHeader col="pcode" label={t('loyalty.products.columns.code')} />
								</th>
								<th className="px-3 py-2 text-left">
									<SortHeader col="bcd" label={t('loyalty.products.columns.barcode')} />
								</th>
								<th className="px-3 py-2 text-right">
									<SortHeader col="pnt" label={t('loyalty.products.columns.points')} className="justify-end" />
								</th>
								<th className="px-3 py-2 text-left">
									<SortHeader col="afrom" label={t('loyalty.products.columns.activeFrom')} />
								</th>
								<th className="px-3 py-2 text-left">
									<SortHeader col="ato" label={t('loyalty.products.columns.activeTo')} />
								</th>
							</tr>
						</thead>
						<tbody>
							{rows.map(p => (
								<tr key={p.id} className="border-b border-border/60 hover:bg-muted/50">
									<td className="px-3 py-2 font-medium text-foreground">{p.pname || '—'}</td>
									<td className="px-3 py-2 font-mono text-xs text-foreground-muted">{p.pcode || '—'}</td>
									<td className="px-3 py-2 font-mono text-xs text-foreground-muted">{p.bcd || '—'}</td>
									<td className="px-3 py-2 text-right tabular-nums text-foreground">{p.pnt ?? 0}</td>
									<td className="px-3 py-2 text-foreground-muted">{formatDate(p.afrom)}</td>
									<td className="px-3 py-2 text-foreground-muted">{formatDate(p.ato)}</td>
								</tr>
							))}
						</tbody>
					</table>
				)}
			</div>

			<div className="flex flex-wrap items-center justify-between gap-3 text-sm text-foreground-muted">
				<div className="flex items-center gap-2">
					<span>{t('system.grid.rowsPerPage')}</span>
					<select
						value={pageSize}
						onChange={e => {
							setPageSize(Number(e.target.value));
							setPage(0);
						}}
						className="h-8 rounded-md border border-border bg-surface px-2">
						{PAGE_SIZES.map(s => (
							<option key={s} value={s}>
								{s}
							</option>
						))}
					</select>
				</div>
				<div className="flex items-center gap-2">
					<span>{t('loyalty.products.pageInfo', { page: page + 1, pages: pageCount, total })}</span>
					<Button variant="outline" size="sm" disabled={page <= 0} onClick={() => setPage(p => Math.max(0, p - 1))}>
						{t('common.previous')}
					</Button>
					<Button variant="outline" size="sm" disabled={page >= pageCount - 1} onClick={() => setPage(p => p + 1)}>
						{t('common.next')}
					</Button>
				</div>
			</div>
		</div>
	);
}
